// ============================================
// Public Tracking Page — Premium Hyper-Glass Design
// Accessible via /track/:token (no auth required)
// ============================================

import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import socketService from '../../services/socket';
import { GoogleMap, Marker } from '@react-google-maps/api';
import { useGoogleMaps } from '../../hooks/useGoogleMaps';

const mapContainerStyle = {
    width: '100%',
    height: '100%'
};

const PublicTracking = () => {
    const { token } = useParams();
    const [loading, setLoading] = useState(true);
    const [routeData, setRouteData] = useState(null);
    const [driverLoc, setDriverLoc] = useState(null);
    const [address, setAddress] = useState('Acquiring GPS Signal...');
    const [trackingError, setTrackingError] = useState(null);
    const [lastUpdated, setLastUpdated] = useState(null);

    const { isLoaded } = useGoogleMaps();

    // Reverse geocode driver location to street address
    useEffect(() => {
        if (!isLoaded || !driverLoc || !window.google?.maps?.Geocoder) return;
        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ location: { lat: driverLoc[1], lng: driverLoc[0] } }, (results, status) => {
            if (status === 'OK' && results[0]) {
                setAddress(results[0].formatted_address);
            } else {
                setAddress("GPS Signal Active • Navigating...");
            }
        });
        setLastUpdated(new Date().toLocaleTimeString());
    }, [isLoaded, driverLoc]);

    useEffect(() => {
        let handleLocationUpdate = null;

        const fetchRoute = async () => {
            try {
                const res = await axios.get(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/public/track/${token}`);
                if (res.data.success) {
                    const data = res.data.data;
                    setRouteData(data);
                    if (data.driverId?.currentLocation?.coordinates) {
                        setDriverLoc(data.driverId.currentLocation.coordinates);
                    }

                    // Connect socket to follow driver
                    socketService.connect('user', 'public-parent');
                    
                    handleLocationUpdate = (update) => {
                        if (update.userId === data.driverId?._id) {
                            setDriverLoc([update.lng, update.lat]);
                        }
                    };
                    socketService.on('driver:location:update', handleLocationUpdate);
                }
            } catch (err) {
                setTrackingError(err.response?.data?.message || 'Tracking link expired or invalid');
            } finally {
                setLoading(false);
            }
        };
        fetchRoute();

        return () => {
            if (handleLocationUpdate) {
                socketService.off('driver:location:update', handleLocationUpdate);
            }
            socketService.disconnect();
        };
    }, [token]);

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-950 via-[#0a1628] to-slate-950 flex flex-col items-center justify-center p-6 text-center">
                <div className="relative">
                    <div className="w-20 h-20 border-4 border-emerald-500/20 border-t-emerald-500 rounded-full animate-spin" />
                    <div className="absolute inset-0 w-20 h-20 border-4 border-transparent border-b-teal-400/40 rounded-full animate-spin" style={{ animationDirection: 'reverse', animationDuration: '1.5s' }} />
                </div>
                <p className="text-slate-400 font-bold text-sm mt-6 animate-pulse tracking-wider uppercase">Establishing Secure Connection...</p>
            </div>
        );
    }

    if (trackingError) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-950 via-[#0a1628] to-slate-950 flex flex-col items-center justify-center p-6 text-center">
                <div className="w-24 h-24 rounded-3xl bg-red-500/10 border border-red-500/20 flex items-center justify-center mb-8 shadow-2xl shadow-red-500/10">
                    <svg className="w-10 h-10 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                    </svg>
                </div>
                <h3 className="text-xl font-black text-white uppercase tracking-tight mb-2">Access Denied</h3>
                <p className="text-slate-500 text-sm font-medium max-w-xs">{trackingError}</p>
            </div>
        );
    }

    const statusConfig = {
        'pending': { label: 'Assigned', color: 'emerald', icon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z' },
        'started': { label: 'On Route', color: 'blue', icon: 'M13 10V3L4 14h7v7l9-11h-7z' },
        'completed': { label: 'Arrived', color: 'green', icon: 'M5 13l4 4L19 7' },
        'cancelled': { label: 'Cancelled', color: 'red', icon: 'M6 18L18 6M6 6l12 12' }
    };
    const currentStatus = statusConfig[routeData?.status] || statusConfig['pending'];

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-950 via-[#0a1628] to-slate-950 flex flex-col max-w-lg mx-auto relative overflow-hidden">
            {/* Ambient Glow Effects */}
            <div className="absolute top-0 left-1/4 w-72 h-72 bg-emerald-500/5 rounded-full blur-[100px] pointer-events-none" />
            <div className="absolute bottom-1/3 right-0 w-64 h-64 bg-teal-500/5 rounded-full blur-[80px] pointer-events-none" />

            {/* Header */}
            <header className="relative z-20 p-5 sm:p-6 flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-lg shadow-emerald-500/30">
                        <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                    </div>
                    <div>
                        <h1 className="text-base font-black text-white tracking-tight">IdaCab Tracking</h1>
                        <p className="text-[9px] text-emerald-400 font-black uppercase tracking-[0.2em]">Live Monitoring</p>
                    </div>
                </div>
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 backdrop-blur-xl">
                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse shadow-[0_0_10px_rgba(16,185,129,0.8)]" />
                    <span className="text-[9px] font-black text-emerald-400 tracking-widest uppercase">Secure</span>
                </div>
            </header>

            {/* Map Area */}
            <div className="relative mx-4 rounded-3xl overflow-hidden shadow-2xl shadow-black/50 border border-white/5" style={{ height: '280px' }}>
                {isLoaded ? (
                    <GoogleMap
                        mapContainerStyle={mapContainerStyle}
                        center={{
                            lat: driverLoc?.[1] || 51.5074,
                            lng: driverLoc?.[0] || -0.1278
                        }}
                        zoom={14}
                        options={{
                            disableDefaultUI: true,
                            zoomControl: true,
                            styles: [
                                { elementType: 'geometry', stylers: [{ color: '#1a1a2e' }] },
                                { elementType: 'labels.text.stroke', stylers: [{ color: '#1a1a2e' }] },
                                { elementType: 'labels.text.fill', stylers: [{ color: '#666680' }] },
                                { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#2a2a3e' }] },
                                { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#0e1626' }] },
                                { featureType: 'poi', stylers: [{ visibility: 'off' }] },
                            ]
                        }}
                    >
                        {driverLoc && (
                            <Marker
                                position={{ lat: driverLoc[1], lng: driverLoc[0] }}
                                icon={{
                                    path: window.google?.maps?.SymbolPath?.CIRCLE || 0,
                                    scale: 12,
                                    fillColor: '#10b981',
                                    fillOpacity: 1,
                                    strokeWeight: 3,
                                    strokeColor: '#ffffff'
                                }}
                            />
                        )}
                    </GoogleMap>
                ) : (
                    <div className="absolute inset-0 flex items-center justify-center bg-slate-900">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500"></div>
                    </div>
                )}
            </div>

            {/* Bottom Status Panel */}
            <div className="relative z-20 flex-1 mt-4 mx-4 mb-4 space-y-4">

                {/* Status + Live Location Combined Card */}
                <div className="bg-white/[0.04] backdrop-blur-2xl rounded-[2rem] border border-white/[0.08] p-5 sm:p-6 shadow-2xl shadow-black/30 space-y-5">
                    
                    {/* Current Status Row */}
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-[9px] text-slate-500 font-black uppercase tracking-[0.2em] mb-1">Current Status</p>
                            <h2 className="text-2xl font-black text-white tracking-tight">{currentStatus.label}</h2>
                        </div>
                        <div className={`w-14 h-14 rounded-2xl bg-${currentStatus.color}-500/10 border border-${currentStatus.color}-500/20 flex items-center justify-center shadow-lg shadow-${currentStatus.color}-500/10`}>
                            <svg viewBox="0 0 24 24" fill="none" className="w-7 h-7 text-emerald-400" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <path d={currentStatus.icon} />
                            </svg>
                        </div>
                    </div>

                    {/* Divider */}
                    <div className="h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />

                    {/* Live Location Status */}
                    <div className="flex items-start gap-4">
                        <div className="relative flex-shrink-0">
                            <div className="w-11 h-11 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
                                <svg viewBox="0 0 24 24" fill="none" className="w-5 h-5 text-emerald-400" stroke="currentColor" strokeWidth="2.5">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                </svg>
                            </div>
                            <div className="absolute -top-0.5 -right-0.5 w-3 h-3 rounded-full bg-emerald-500 border-2 border-slate-950 animate-pulse" />
                        </div>
                        <div className="min-w-0 flex-1">
                            <p className="text-[9px] text-emerald-400 font-black uppercase tracking-[0.2em] mb-1 flex items-center gap-2">
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse shadow-[0_0_8px_rgba(16,185,129,0.6)]" />
                                Live Location
                            </p>
                            <p className="text-sm font-bold text-white/90 leading-snug line-clamp-2">
                                {driverLoc ? address : 'Waiting for Driver GPS Signal...'}
                            </p>
                            {lastUpdated && (
                                <p className="text-[10px] text-slate-600 font-bold mt-1">Last updated: {lastUpdated}</p>
                            )}
                        </div>
                    </div>
                </div>

                {/* Driver & Route Info Cards */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {/* Driver Card */}
                    <div className="flex items-center gap-4 p-4 rounded-2xl bg-white/[0.04] backdrop-blur-xl border border-white/[0.06] hover:bg-white/[0.06] transition-colors">
                        <div className="w-11 h-11 flex-shrink-0 rounded-2xl bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center text-white font-black text-xs shadow-lg shadow-emerald-500/20">
                            {routeData?.driverId?.name?.split(' ').map(n => n[0]).join('') || 'DR'}
                        </div>
                        <div className="min-w-0">
                            <p className="text-[9px] text-slate-500 font-black uppercase tracking-[0.2em]">Your Driver</p>
                            <p className="text-sm font-bold text-white truncate">{routeData?.driverId?.name || 'Assigned Driver'}</p>
                        </div>
                    </div>

                    {/* Route Card */}
                    <div className="flex items-center gap-4 p-4 rounded-2xl bg-white/[0.04] backdrop-blur-xl border border-white/[0.06] hover:bg-white/[0.06] transition-colors">
                        <div className="w-11 h-11 flex-shrink-0 rounded-2xl bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center text-white shadow-lg shadow-blue-500/20">
                            <svg viewBox="0 0 24 24" fill="none" className="w-5 h-5" stroke="currentColor" strokeWidth="2.5">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                            </svg>
                        </div>
                        <div className="min-w-0">
                            <p className="text-[9px] text-slate-500 font-black uppercase tracking-[0.2em]">Route</p>
                            <p className="text-sm font-bold text-white truncate">{routeData?.name || 'Active Route'}</p>
                        </div>
                    </div>
                </div>

                {/* School Info */}
                {routeData?.schoolId?.name && (
                    <div className="flex items-center gap-4 p-4 rounded-2xl bg-white/[0.04] backdrop-blur-xl border border-white/[0.06]">
                        <div className="w-11 h-11 flex-shrink-0 rounded-2xl bg-gradient-to-br from-violet-400 to-purple-500 flex items-center justify-center text-white shadow-lg shadow-violet-500/20">
                            <svg viewBox="0 0 24 24" fill="none" className="w-5 h-5" stroke="currentColor" strokeWidth="2.5">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 14l9-5-9-5-9 5 9 5z" />
                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
                            </svg>
                        </div>
                        <div className="min-w-0">
                            <p className="text-[9px] text-slate-500 font-black uppercase tracking-[0.2em]">School</p>
                            <p className="text-sm font-bold text-white truncate">{routeData.schoolId.name}</p>
                        </div>
                    </div>
                )}

                {/* Refresh Button */}
                <button 
                    onClick={() => window.location.reload()} 
                    className="w-full py-4 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 text-white rounded-2xl font-black text-sm uppercase tracking-widest shadow-xl shadow-emerald-500/20 transition-all active:scale-[0.98] flex items-center justify-center gap-3"
                >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Refresh Tracking
                </button>
            </div>

            {/* Footer */}
            <p className="pb-6 text-center text-[9px] text-slate-700 font-bold tracking-widest uppercase">
                Token: {token?.slice(0, 8)}... • Valid for 6h
            </p>
        </div>
    );
};

export default PublicTracking;
